import { useEffect, useMemo, useRef, useState } from 'react';

import {
    Breadcrumbs,
    CustomHeader,
    MultiSelect,
    Pagination,
    StyledMenuPaper,
    TableNoData,
    TableSkeleton
} from '@armis/armis-ui-library';
import { Menu, PaperProps, Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    DRAFT_INTEGRATION,
    INACTIVE_INTEGRATION,
    INITIALIZING_COLLECTOR,
    PARTNERS,
    PENDING_ACTIVATION_COLLECTOR,
    SEARCH_TENANTS_PLACEHOLDER,
    TENANTS
} from 'src/constants/LabelText';
import { AGGREGATED_VIEW, ANALYTICS, HOME } from 'src/constants/RouteConstants';
import {
    createRelatedObject,
    DEFAULT_PAGE,
    DEFAULT_PAGESIZE,
    NO_DATA_TO_SHOW,
    tenantAggregationAPIMapping
} from 'src/constants/TableConstants';
import { tenantAggregationViewDef } from 'src/helpers/ColumnsConfig';
import {
    convertQueryObjectToParams,
    displayErrorMessage,
    getDirectionsAndProperties
} from 'src/helpers/utility';
import IsLoadingHOC from 'src/hoc/IsLoadingHoc';
import { useTable } from 'src/hooks/useTable';
import { Header } from 'src/pages/components/Header';
import SearchBar from 'src/pages/components/SearchBar/SearchBar';
import { TableHeader } from 'src/pages/components/TableHeader';
import {
    TenantAggregationViewProps,
    TenantAggregationViewData
} from 'src/pages/containers/AggregatedView/TenantAggregationView.types';
import {
    getPartnerByID,
    getTenantAggregationData
} from 'src/services/api.service';
import { selectUser } from 'src/store/slices/userSlice';
import { ChildRefProp, FilterItems, Map } from 'src/types/CommonTypes';

import { StyledTable } from './TenantAggregationView.style';

const tenantSortOrder: Map<number> = {};
const tenantSortStatus: Map<string> = {};
const columnsFilterItems: FilterItems[] = [];

const hideColumns = [
    INACTIVE_INTEGRATION,
    DRAFT_INTEGRATION,
    INITIALIZING_COLLECTOR,
    PENDING_ACTIVATION_COLLECTOR
];
createRelatedObject(
    tenantAggregationAPIMapping,
    tenantSortOrder,
    tenantSortStatus,
    columnsFilterItems,
    hideColumns
);

const TenantAggregationViewComponent = ({
    setIsLoading
}: TenantAggregationViewProps) => {
    const {
        tableLoading,
        setTableLoading,
        columnSortOrder,
        columnSortStatus,
        gridRef,
        filterItems,
        anchorEl,
        onSortChangedCall,
        handleMenuClick,
        handleMenuClose,
        onSelectionChanged,
        queryProperties,
        setQueryProperties
    } = useTable({
        sortOrderObj: tenantSortOrder,
        sortStatusObj: tenantSortStatus,
        columnsFilterItems
    });
    const navigate = useNavigate();
    const { partnerId } = useParams();
    const [rowData, setRowData] = useState<TenantAggregationViewData[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const paginationRef = useRef<ChildRefProp>();
    const firstRender = useRef(true);
    const currentPartner = useRef('');

    const currentUser = useSelector(selectUser);

    const isArmisUser = currentUser.username.includes('@armis.com');

    useEffect(() => {
        setIsLoading(true, true);
        getPartnerByID(partnerId as string)
            .then(res => {
                currentPartner.current = res.data.name;
            })
            .catch(() => {
                navigate(`${ANALYTICS}/${AGGREGATED_VIEW}`, { replace: true });
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [partnerId, navigate, setIsLoading]);

    const defaultColDefs = useMemo(
        () => ({
            headerComponent: CustomHeader,
            headerComponentParams: {
                onSortChanged: onSortChangedCall,
                columnSortOrder,
                columnSortStatus
            },
            sortable: true,
            resizable: true
        }),
        [columnSortOrder, columnSortStatus]
    );

    useEffect(() => {
        let totalElements = 0;
        setTableLoading(true);
        getTenantAggregationData(
            partnerId!,
            convertQueryObjectToParams(queryProperties)
        )
            .then((res: AxiosResponse) => {
                setRowData(res.data.content);
                totalElements = res.data.totalElements;
                setTotalRows(totalElements);
            })
            .catch(err => {
                displayErrorMessage(err);
                setRowData([]);
                setTotalRows(0);
            })
            .finally(() => {
                setTableLoading(false);
                if (
                    queryProperties.page === DEFAULT_PAGE &&
                    queryProperties.size === DEFAULT_PAGESIZE
                )
                    paginationRef.current?.resetPagination();
            });
    }, [queryProperties, setTableLoading, partnerId]);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        const { directions, properties } = getDirectionsAndProperties(
            columnSortOrder,
            columnSortStatus
        );
        setQueryProperties(prevValue => ({
            ...prevValue,
            directions,
            properties,
            page: DEFAULT_PAGE,
            size: DEFAULT_PAGESIZE
        }));
    }, [columnSortStatus, columnSortOrder, setQueryProperties]);

    const searchPartnerContainer = useMemo(
        () => (
            <div>
                <SearchBar
                    onChange={newValue =>
                        setQueryProperties(prevValue => ({
                            ...prevValue,
                            searchBy: newValue,
                            page: DEFAULT_PAGE,
                            size: DEFAULT_PAGESIZE
                        }))
                    }
                    placeholder={SEARCH_TENANTS_PLACEHOLDER}
                    searchValue={queryProperties.searchBy as string}
                />
            </div>
        ),
        [queryProperties.searchBy]
    );

    const gridReady = () => {
        hideColumns.forEach(element =>
            gridRef.current?.columnApi?.setColumnVisible(element, false)
        );
    };

    useEffect(() => {
        if (!gridRef.current?.api) return;
        if (rowData.length === 0 && !tableLoading && !firstRender.current) {
            setTimeout(() => {
                gridRef.current?.api.showNoRowsOverlay();
            }, 100);
        }
    }, [rowData, tableLoading, gridRef]);

    return (
        <>
            <Header>
                <Breadcrumbs>
                    <Link className="parent-link" to={HOME}>
                        {PARTNERS}
                    </Link>
                    <Typography
                        style={{
                            fontFamily: 'Proxima Nova Bld'
                        }}
                        variant="h4"
                    >
                        {currentPartner.current}
                    </Typography>
                </Breadcrumbs>
            </Header>
            <div className="control table">
                <TableHeader
                    childrenLeft={<> {searchPartnerContainer}</>}
                    loading={tableLoading}
                    onColumnMenuClick={handleMenuClick}
                    title={`${totalRows} ${TENANTS}`}
                />
                <Menu
                    anchorEl={anchorEl}
                    onClose={handleMenuClose}
                    open={Boolean(anchorEl)}
                    PaperProps={
                        {
                            component: StyledMenuPaper
                        } as Partial<PaperProps<'div', {}>> | undefined
                    }
                >
                    <MultiSelect
                        items={filterItems}
                        onSelectionChanged={onSelectionChanged}
                        showSelectAllOption
                    />
                </Menu>
                <StyledTable
                    ref={gridRef}
                    columnDefs={tenantAggregationViewDef}
                    context={{ isArmisUser }}
                    defaultColDef={defaultColDefs}
                    headerHeight={35}
                    loadingOverlayComponent={TableSkeleton}
                    loadingOverlayComponentParams={{
                        style: { top: '70px' }
                    }}
                    noRowsOverlayComponent={TableNoData}
                    noRowsOverlayComponentParams={{
                        content: NO_DATA_TO_SHOW
                    }}
                    onGridReady={gridReady}
                    rowData={rowData}
                    suppressNoRowsOverlay={firstRender.current}
                />
                {!!totalRows && totalRows > 0 && (
                    <Pagination
                        ref={paginationRef}
                        onPaginationChanged={(page, size) => {
                            setQueryProperties(prevValue => ({
                                ...prevValue,
                                page,
                                size
                            }));
                        }}
                        totalRowsCount={totalRows}
                    />
                )}
            </div>
        </>
    );
};
export const TenantAggregationView = IsLoadingHOC(
    TenantAggregationViewComponent
);
